import { createTheme } from "@mui/material/styles";

// Same tokens as src/assets/css/theme.css (the public Fribe site's design system).
const tokens = {
  ink: "#1b1233",
  ink2: "#4b4361",
  ink3: "#6f6885",
  line: "#e6e1f0",
  lineStrong: "#d3cbe4",
  land: "#f3f0fa",
  brand: "#6e00dc",
  brandHover: "#5b00b6",
  brandTint: "#f4ecff",
  brandTint2: "#e8d6ff",
  flow: "#13946a",
  slow: "#c97d0b",
  jam: "#d63a41",
  shadowFloat: "0 24px 60px -18px rgba(27, 18, 51, .28), 0 3px 8px rgba(27, 18, 51, .06)",
  shadowPop: "0 12px 32px -10px rgba(27, 18, 51, .22), 0 2px 4px rgba(27, 18, 51, .05)",
};

const fontBody = '"Nunito Sans", "Nunito Sans Variable", system-ui, -apple-system, "Segoe UI", sans-serif';
const fontDisplay = '"Bricolage Grotesque", "Bricolage Grotesque Variable", "Nunito Sans", system-ui, sans-serif';
const ease = "cubic-bezier(.22, 1, .36, 1)";

const display = { fontFamily: fontDisplay, fontWeight: 700, letterSpacing: "-0.02em" };

const theme = createTheme({
  palette: {
    primary: { main: tokens.brand, dark: tokens.brandHover, light: "#8f46d8", contrastText: "#fff" },
    secondary: { main: tokens.ink2 },
    error: { main: tokens.jam },
    success: { main: tokens.flow },
    warning: { main: tokens.slow },
    text: { primary: tokens.ink, secondary: tokens.ink2, disabled: tokens.ink3 },
    divider: tokens.line,
    background: { default: "#fff", paper: "#fff" },
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: fontBody,
    h1: display,
    h2: display,
    h3: display,
    h4: display,
    h5: display,
    h6: display,
    subtitle1: { fontWeight: 700 },
    subtitle2: { fontWeight: 700 },
    button: { textTransform: "none", fontWeight: 700 },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 999,
          textTransform: "none",
          fontWeight: 700,
          transition: `background-color .25s ${ease}, color .25s ${ease}, border-color .25s ${ease}, transform .25s ${ease}, box-shadow .25s ${ease}`,
          "&:hover": { transform: "translateY(-1px)" },
          "&:active": { transform: "translateY(0) scale(.985)" },
        },
        containedPrimary: {
          "&:hover": { backgroundColor: tokens.brandHover, boxShadow: "0 10px 24px -10px rgba(110, 0, 220, .6)" },
        },
        outlined: {
          borderWidth: 1.5,
          borderColor: tokens.lineStrong,
          color: tokens.ink,
          "&:hover": { borderWidth: 1.5, borderColor: tokens.ink, backgroundColor: "transparent" },
        },
        outlinedPrimary: {
          borderColor: tokens.brand,
          color: tokens.brand,
          "&:hover": { borderColor: tokens.brand, backgroundColor: tokens.brandTint },
        },
        text: { "&:hover": { backgroundColor: tokens.brandTint } },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: `background-color .25s ${ease}, color .25s ${ease}, transform .25s ${ease}`,
          "&:hover": { backgroundColor: tokens.brandTint },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 24, boxShadow: tokens.shadowFloat, backgroundImage: "none" },
      },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { ...display, fontSize: "1.25rem" } },
    },
    MuiBackdrop: {
      styleOverrides: { root: { backgroundColor: "rgba(27, 18, 51, .45)" } },
    },
    MuiPaper: {
      styleOverrides: { rounded: { borderRadius: 16 } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: "#fff",
          transition: `box-shadow .25s ${ease}`,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: tokens.lineStrong, borderWidth: 1.5 },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: tokens.ink3 },
          "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(110, 0, 220, .18)" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: tokens.brand, borderWidth: 1.5 },
          "&.Mui-error.Mui-focused": { boxShadow: "0 0 0 4px rgba(214, 58, 65, .18)" },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontWeight: 600, color: tokens.ink3, "&.Mui-focused": { color: tokens.brand } } },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: { "&.Mui-checked + .MuiSwitch-track": { backgroundColor: tokens.brand, opacity: 0.5 } },
      },
    },
    MuiCheckbox: {
      defaultProps: { color: "primary" },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: 16, boxShadow: tokens.shadowPop, border: `1px solid ${tokens.line}` },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          margin: "0 6px",
          fontWeight: 600,
          "&:hover": { backgroundColor: tokens.land },
          "&.Mui-selected": { backgroundColor: tokens.brandTint, color: tokens.brand },
          "&.Mui-selected:hover": { backgroundColor: tokens.brandTint2 },
        },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: tokens.line } },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { color: tokens.ink2 },
        selectLabel: { fontWeight: 600 },
        displayedRows: { fontWeight: 600 },
      },
    },
  },
});

export default theme;
